import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Edit as EditIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../../api';
import { toast } from 'react-toastify';

const GameContentView = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`admin/game-contents/${id}`)
      .then((r) => {
        if (r.data?.success && r.data?.data) setData(r.data.data);
        else setData(null);
      })
      .catch(() => {
        toast.error('Failed to load game content');
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Game content not found.</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/game-contents')} sx={{ mt: 2 }}>
          Back to list
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/game-contents')}>
          Back
        </Button>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/admin/game-contents/${id}/edit`)}
          sx={{ bgcolor: '#5e35b1', '&:hover': { bgcolor: '#4527a0' } }}
        >
          Edit
        </Button>
        <Typography component="h1" sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 700, color: '#111827', lineHeight: 1.3, mb: 0.5 }}>
          Game Content Details
        </Typography>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle2" color="text.secondary">Game type</Typography>
        <Chip label={data.game_type} size="small" sx={{ mt: 0.5, mb: 2 }} />

        <Typography variant="subtitle2" color="text.secondary">Subject</Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>{data.subject_name || '—'}</Typography>

        <Typography variant="subtitle2" color="text.secondary">Topic</Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>{data.topic_name || '—'}</Typography>

        <Typography variant="subtitle2" color="text.secondary">Title</Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>{data.title || '—'}</Typography>

        <Typography variant="subtitle2" color="text.secondary">Difficulty</Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>{data.difficulty || '—'}</Typography>

        <Typography variant="subtitle2" color="text.secondary">Status</Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>{data.status === 1 ? 'Active' : 'Inactive'}</Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Content (JSON)</Typography>
        <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa', overflow: 'auto' }}>
          <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {data.content != null ? JSON.stringify(data.content, null, 2) : '—'}
          </Typography>
        </Paper>

        <Divider sx={{ my: 2 }} />
        <Typography variant="caption" color="text.secondary">
          Created: {data.created_at ? new Date(data.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'} ·
          Updated: {data.updated_at ? new Date(data.updated_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
        </Typography>
      </Paper>
    </Box>
  );
};

export default GameContentView;
